/**
 * Šprtec 3D — hlavní řídicí modul klienta.
 * Propojuje UI obrazovky, 3D scénu, lokální hru (single/duel) a online hru.
 */

import { Scene3D } from './scene3d.js';
import { Net, fetchLeaderboard } from './net.js';
import { Sfx } from './audio.js';
import { Game } from '../shared/game.js';
import { chooseShot } from '../shared/ai.js';
import { MAX_SHOT_SPEED, MIN_SHOT_SPEED, PIECE_R, makeBodies } from '../shared/physics.js';

const $ = (sel) => document.querySelector(sel);
const $$ = (sel) => [...document.querySelectorAll(sel)];

const EMOTES = ['👍', '🔥', '😅', '🥅', 'GG', 'Dobrá!'];
const TURN_MS = 35_000;
const AIM_MAX_DRAG = 0.62; // metry tažení = plná síla

// ---------------------------------------------------------------------------
// Mobil: fullscreen (Android), zámek orientace, wake lock, vibrace
// ---------------------------------------------------------------------------

const IS_TOUCH = window.matchMedia?.('(pointer: coarse)').matches ?? false;
const FS_SUPPORTED = Boolean(document.documentElement.requestFullscreen);

function enterFullscreen() {
  if (!FS_SUPPORTED || document.fullscreenElement) return;
  document.documentElement
    .requestFullscreen({ navigationUI: 'hide' })
    .then(() => {
      // na Androidu po fullscreenu zamkneme portrét (jinde tiše selže)
      try {
        screen.orientation?.lock?.('portrait').catch(() => {});
      } catch {
        /* orientace není podporovaná */
      }
    })
    .catch(() => {});
}

function toggleFullscreen() {
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  else enterFullscreen();
}

/** Fullscreen automaticky jen na dotykovém zařízení (musí jít o gesto). */
function autoFullscreen() {
  if (IS_TOUCH) enterFullscreen();
}

let wakeLock = null;
async function acquireWakeLock() {
  try {
    wakeLock = await navigator.wakeLock?.request('screen');
  } catch {
    /* wake lock je jen vylepšení */
  }
}
function releaseWakeLock() {
  wakeLock?.release().catch(() => {});
  wakeLock = null;
}
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && S.mode) acquireWakeLock();
});

/** Krátké zavibrování na dotykových zařízeních. */
function buzz(pattern) {
  if (IS_TOUCH) navigator.vibrate?.(pattern);
}

// ---------------------------------------------------------------------------
// Stav aplikace
// ---------------------------------------------------------------------------

const S = {
  mode: null, // null | 'sp' | 'local' | 'mp'
  aiLevel: localStorage.getItem('sprtec2_level') || 'medium',
  name: localStorage.getItem('sprtec2_name') || '',

  game: null,        // lokální Game (sp/local)
  aiTimer: null,

  net: null,         // online
  mySide: 0,
  names: ['', ''],
  phase: 'aim',
  turn: 0,
  score: [0, 0],
  deadline: 0,
  scoreLimit: 5,
  mpBodies: makeBodies(), // kopie pro míření/AI cast v online hře

  aiming: null,      // { pieceId, piece:{x,z}, dir, power }
  camMode: 'side',
  ended: false,
};

const scene = new Scene3D($('#gl'));
const sfx = new Sfx();

// ---------------------------------------------------------------------------
// Pomocné UI funkce
// ---------------------------------------------------------------------------

let toastTimer = null;
function toast(msg, ms = 2600) {
  const el = $('#toast');
  el.textContent = msg;
  el.classList.remove('hidden');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.add('hidden'), ms);
}

function showScreen(id) {
  for (const s of $$('.screen')) s.classList.add('hidden');
  if (id) $(id).classList.remove('hidden');
}

function show(id, visible) {
  $(id).classList.toggle('hidden', !visible);
}

function myName() {
  return $('#name-input').value.trim() || 'Anonym';
}

// ---------------------------------------------------------------------------
// HUD
// ---------------------------------------------------------------------------

function isMyTurn() {
  if (S.phase !== 'aim') return false;
  if (S.mode === 'sp') return S.turn === 0;
  if (S.mode === 'local') return true;
  if (S.mode === 'mp') return S.turn === S.mySide;
  return false;
}

function controlledSide() {
  if (S.mode === 'sp') return 0;
  if (S.mode === 'local') return S.turn;
  return S.mySide;
}

function updateHud() {
  $('#hud-name0').textContent = S.names[0];
  $('#hud-name1').textContent = S.names[1];
  $('#hud-score').textContent = `${S.score[0]} : ${S.score[1]}`;

  const banner = $('#turn-banner');
  if (S.phase === 'sim' || S.phase === 'goal' || S.phase === 'over') {
    banner.textContent = '…';
    banner.classList.remove('my-turn');
  } else if (S.mode === 'local') {
    const who = S.turn === 0 ? 'Červení' : 'Modří';
    banner.textContent = `Na tahu: ${who}`;
    banner.classList.add('my-turn');
  } else if (isMyTurn()) {
    banner.textContent = 'Jsi na tahu!';
    banner.classList.add('my-turn');
  } else {
    banner.textContent = S.mode === 'sp' ? 'Počítač přemýšlí…' : 'Soupeř hraje…';
    banner.classList.remove('my-turn');
  }

  scene.setHighlights(isMyTurn() ? controlledSide() : null);

  $('#hud-hint').textContent = isMyTurn()
    ? 'Táhni od svého kamene a pusť'
    : '';
  show('#hud-hint', Boolean($('#hud-hint').textContent));
}

function updateTimer() {
  if (S.mode !== 'mp' || !S.deadline || S.phase !== 'aim') {
    show('#turn-timer', false);
    return;
  }
  show('#turn-timer', true);
  const remain = Math.max(0, S.deadline - Date.now());
  const pct = Math.min(100, (remain / TURN_MS) * 100);
  const fill = $('#turn-timer-fill');
  fill.style.width = `${pct}%`;
  fill.classList.toggle('low', remain < 8000);
}

// ---------------------------------------------------------------------------
// Herní události (společné pro lokální i online hru)
// ---------------------------------------------------------------------------

function handleEvents(events) {
  for (const e of events) {
    switch (e.t) {
      case 'hit':
        sfx.hit(e.s);
        buzz(Math.round(8 + e.s * 25));
        break;
      case 'wall':
        sfx.wall(e.s);
        buzz(6);
        break;
      case 'goal': {
        S.score = e.score;
        sfx.goal();
        buzz([70, 50, 140]);
        scene.goalPulse(e.scorer);
        $('#goal-sub').textContent = `${S.names[e.scorer]} — ${e.score[0]} : ${e.score[1]}`;
        show('#overlay-goal', true);
        setTimeout(() => show('#overlay-goal', false), 2100);
        break;
      }
      case 'reset':
        scene.teleportNext = S.mode === 'mp';
        break;
      case 'turn':
        S.turn = e.side;
        if (S.mode === 'sp' && e.side === 1) scheduleAi();
        break;
      case 'over':
        S.score = e.score;
        gameOver(e.winner, e.reason ?? 'score');
        break;
      default:
        break;
    }
  }
  if (events.length) updateHud();
}

function gameOver(winner, reason) {
  S.ended = true;
  S.phase = 'over';
  cancelAim();

  let title;
  let sub = `Konečné skóre ${S.score[0]} : ${S.score[1]}`;
  if (S.mode === 'local') {
    title = winner === 0 ? '🏆 Vyhráli Červení!' : '🏆 Vyhráli Modří!';
    sfx.win();
  } else {
    const meWon = winner === (S.mode === 'sp' ? 0 : S.mySide);
    title = meWon ? '🏆 Vyhrál jsi!' : '😞 Prohrál jsi';
    if (reason === 'forfeit') sub = 'Soupeř opustil zápas.';
    if (meWon) sfx.win();
    else sfx.lose();
  }
  $('#end-title').textContent = title;
  $('#end-sub').textContent = sub;
  $('#btn-rematch').textContent = S.mode === 'mp' ? '🔄 Odveta' : '🔄 Hrát znovu';
  show('#btn-rematch', reason !== 'forfeit');
  setTimeout(() => show('#overlay-end', true), reason === 'score' ? 2300 : 400);
}

// ---------------------------------------------------------------------------
// Lokální hra (singleplayer / dva hráči)
// ---------------------------------------------------------------------------

function startLocalGame(mode) {
  S.mode = mode;
  S.game = new Game({});
  S.ended = false;
  S.score = [0, 0];
  S.phase = 'aim';
  S.turn = S.game.turn;
  S.deadline = 0;
  S.names = mode === 'sp' ? [myName(), '🤖 Počítač'] : ['Červení', 'Modří'];
  S.mySide = 0;

  scene.teleportNext = true;
  scene.setBodiesDirect(S.game.bodies);
  scene.setCamera(0, mode === 'local' ? 'top' : 'side', true);
  S.camMode = mode === 'local' ? 'top' : 'side';

  showScreen(null);
  show('#hud', true);
  show('#emote-bar', false);
  show('#overlay-end', false);
  sfx.whistle();
  acquireWakeLock();
  updateHud();
  if (mode === 'sp' && S.turn === 1) scheduleAi();
}

function scheduleAi() {
  clearTimeout(S.aiTimer);
  S.aiTimer = setTimeout(() => {
    if (S.mode !== 'sp' || !S.game) return;
    if (S.game.phase !== 'aim' || S.game.turn !== 1) return;
    const mv = chooseShot(S.game, 1, S.aiLevel);
    if (mv && S.game.applyShot(1, mv.pieceId, mv.vx, mv.vz)) {
      S.phase = 'sim';
      updateHud();
    } else {
      S.game.forceTurnSwitch();
      S.turn = S.game.turn;
      updateHud();
    }
  }, 850 + Math.random() * 700);
}

// ---------------------------------------------------------------------------
// Online hra
// ---------------------------------------------------------------------------

async function goOnline() {
  if (S.net?.connected) {
    S.net.send({ t: 'hello', name: myName() });
    showScreen('#screen-lobby');
    refreshLeaderboard();
    return;
  }
  S.net = new Net({
    onMessage: onNetMessage,
    onClose: () => {
      toast('Spojení se serverem bylo přerušeno');
      leaveToMenu();
    },
  });
  try {
    await S.net.connect();
  } catch {
    toast('Server je nedostupný, zkus to později');
    S.net = null;
    return;
  }
  S.net.send({ t: 'hello', name: myName() });
  showScreen('#screen-lobby');
  refreshLeaderboard();
}

function onNetMessage(msg) {
  switch (msg.t) {
    case 'rooms':
      renderRooms(msg.rooms);
      break;

    case 'created':
      $('#wait-title').textContent = 'Čekám na soupeře…';
      show('#wait-code-box', true);
      $('#wait-code').textContent = msg.code;
      showScreen('#screen-wait');
      break;

    case 'queued':
      $('#wait-title').textContent = 'Hledám soupeře…';
      show('#wait-code-box', false);
      showScreen('#screen-wait');
      break;

    case 'start':
      startOnlineMatch(msg);
      break;

    case 'state': {
      scene.setBodiesTargets(msg.snap.bodies);
      syncMpBodies(msg.snap.bodies);
      S.phase = msg.snap.phase;
      S.score = msg.snap.score;
      if (msg.snap.turn !== S.turn) S.turn = msg.snap.turn;
      if (msg.deadline) S.deadline = msg.deadline;
      handleEvents(msg.ev ?? []);
      updateHud();
      break;
    }

    case 'turn':
      S.turn = msg.side;
      S.phase = 'aim';
      S.deadline = msg.deadline;
      if (msg.timeout) {
        sfx.whistle();
        toast(S.turn === S.mySide ? 'Soupeři vypršel čas — hraješ!' : 'Vypršel ti čas, tah propadá');
      }
      updateHud();
      break;

    case 'shot':
      if (msg.side !== S.mySide) sfx.click();
      break;

    case 'rematchState':
      $('#end-sub').textContent = `Odveta: ${msg.votes}/2 hlasů`;
      break;

    case 'oppLeft':
      if (S.mode === 'mp' && !S.ended) {
        gameOver(S.mySide, 'forfeit');
      } else {
        toast('Soupeř odešel');
        showScreen('#screen-lobby');
      }
      break;

    case 'emote': {
      const el = $('#emote-bubble');
      el.textContent = EMOTES[msg.k] ?? '👋';
      el.classList.remove('hidden');
      clearTimeout(el._t);
      el._t = setTimeout(() => el.classList.add('hidden'), 2200);
      sfx.click();
      break;
    }

    case 'err':
      toast(msg.msg || 'Chyba');
      break;

    default:
      break;
  }
}

function startOnlineMatch(msg) {
  S.mode = 'mp';
  S.game = null;
  S.ended = false;
  S.mySide = msg.side;
  S.names = msg.names;
  S.score = [0, 0];
  S.phase = 'aim';
  S.turn = msg.turn;
  S.deadline = msg.deadline;
  S.scoreLimit = msg.scoreLimit;

  scene.teleportNext = true;
  scene.setBodiesTargets(msg.snap.bodies);
  syncMpBodies(msg.snap.bodies);
  scene.setCamera(S.mySide, 'side', true);
  S.camMode = 'side';

  showScreen(null);
  show('#hud', true);
  show('#emote-bar', true);
  show('#overlay-end', false);
  sfx.whistle();
  acquireWakeLock();
  const color = S.mySide === 0 ? 'červené' : 'modré';
  toast(`Hraješ za ${color} • do ${msg.scoreLimit} gólů`);
  updateHud();
}

/** Udržuje lokální kopii těles pro míření a vodicí linku v online hře. */
function syncMpBodies(snapBodies) {
  for (const [id, x, z] of snapBodies) {
    const b = S.mpBodies.find((bb) => bb.id === id);
    if (b) {
      b.x = x;
      b.z = z;
    }
  }
}

function renderRooms(roomList) {
  const ul = $('#room-list');
  ul.innerHTML = '';
  if (!roomList.length) {
    ul.innerHTML = '<li class="empty">Žádné otevřené místnosti…</li>';
    return;
  }
  for (const r of roomList) {
    const li = document.createElement('li');
    const label = document.createElement('span');
    label.textContent = `${r.host} `;
    const code = document.createElement('span');
    code.className = 'code';
    code.textContent = r.code;
    const btn = document.createElement('button');
    btn.className = 'btn btn-small';
    btn.textContent = 'Hrát';
    btn.onclick = () => {
      sfx.click();
      S.net.send({ t: 'join', code: r.code });
    };
    label.appendChild(code);
    li.append(label, btn);
    ul.appendChild(li);
  }
}

async function refreshLeaderboard() {
  try {
    const rows = await fetchLeaderboard();
    const tbody = $('#leaderboard tbody');
    tbody.innerHTML = '';
    if (!rows.length) {
      tbody.innerHTML = '<tr><td class="empty">Zatím žádné výsledky…</td></tr>';
      return;
    }
    rows.forEach((r, i) => {
      const tr = document.createElement('tr');
      const skore = `${r.goals_for}:${r.goals_against}`;
      tr.innerHTML = `<td>${i + 1}.</td><td></td><td class="num">${r.wins} výher</td><td class="num">${skore}</td>`;
      tr.children[1].textContent = r.name;
      tbody.appendChild(tr);
    });
  } catch {
    /* žebříček je nepovinný */
  }
}

// ---------------------------------------------------------------------------
// Míření a střelba (myš + dotyk přes Pointer Events)
// ---------------------------------------------------------------------------

function currentBodies() {
  return S.mode === 'mp' ? S.mpBodies : S.game?.bodies;
}

function onPointerDown(e) {
  sfx.ensure();
  if (!isMyTurn()) return;
  const p = scene.pickTablePoint(e.clientX, e.clientY);
  if (!p) return;

  const side = controlledSide();
  const bodies = currentBodies();
  if (!bodies) return;

  // prst je méně přesný než kurzor — na dotyku větší dosah výběru
  const grabR = PIECE_R * (e.pointerType === 'touch' ? 3.4 : 2.4);
  let best = null;
  for (const b of bodies) {
    if (b.kind !== 'piece' || b.side !== side) continue;
    const d = Math.hypot(b.x - p.x, b.z - p.z);
    if (d < grabR && (!best || d < best.d)) best = { b, d };
  }
  if (!best) return;

  S.aiming = { pieceId: best.b.id, piece: { x: best.b.x, z: best.b.z, id: best.b.id }, dir: null, power: 0 };
  $('#gl').setPointerCapture(e.pointerId);
  show('#power-bar', true);
}

function onPointerMove(e) {
  if (!S.aiming) return;
  const p = scene.pickTablePoint(e.clientX, e.clientY);
  if (!p) return;

  const a = S.aiming;
  const dx = a.piece.x - p.x; // prak: střílí se proti směru tažení
  const dz = a.piece.z - p.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 0.02) {
    a.dir = null;
    a.power = 0;
    scene.hideAim();
    $('#power-fill').style.width = '0%';
    return;
  }
  a.dir = { x: dx / dist, z: dz / dist };
  a.power = Math.min(1, dist / AIM_MAX_DRAG);
  scene.showAim(a.piece, a.dir, a.power, currentBodies());
  $('#power-fill').style.width = `${Math.round(a.power * 100)}%`;
}

function onPointerUp() {
  const a = S.aiming;
  cancelAim();
  if (!a || !a.dir || a.power < 0.08) return;

  const speed = Math.max(MIN_SHOT_SPEED + 0.05, a.power * MAX_SHOT_SPEED);
  const vx = a.dir.x * speed;
  const vz = a.dir.z * speed;

  if (S.mode === 'mp') {
    S.net.send({ t: 'shot', piece: a.pieceId, vx, vz });
    S.phase = 'sim'; // optimisticky, server vzápětí pošle stav
  } else {
    const side = controlledSide();
    if (S.game.applyShot(side, a.pieceId, vx, vz)) {
      S.phase = 'sim';
    }
  }
  sfx.click();
  updateHud();
}

function cancelAim() {
  S.aiming = null;
  scene.hideAim();
  show('#power-bar', false);
  $('#power-fill').style.width = '0%';
}

const canvas = $('#gl');
canvas.addEventListener('pointerdown', onPointerDown);
canvas.addEventListener('pointermove', onPointerMove);
canvas.addEventListener('pointerup', onPointerUp);
canvas.addEventListener('pointercancel', cancelAim);
canvas.addEventListener('contextmenu', (e) => e.preventDefault());

// ---------------------------------------------------------------------------
// Navigace mezi obrazovkami
// ---------------------------------------------------------------------------

function leaveToMenu() {
  clearTimeout(S.aiTimer);
  cancelAim();
  releaseWakeLock();
  S.mode = null;
  S.game = null;
  S.deadline = 0;
  if (S.net) {
    S.net.close();
    S.net = null;
  }
  show('#hud', false);
  show('#overlay-end', false);
  show('#overlay-goal', false);
  show('#emote-bar', false);
  showScreen('#screen-menu');
  scene.setHighlights(null);
  scene.setCamera(0, 'side', true);
  // atrakt: prázdný stůl v základním rozestavení
  const idle = makeBodies();
  scene.teleportNext = true;
  scene.setBodiesDirect(idle);
}

$('#name-input').value = S.name;
$('#name-input').addEventListener('change', () => {
  localStorage.setItem('sprtec2_name', $('#name-input').value.trim());
});

$$('#difficulty button').forEach((btn) => {
  if (btn.dataset.level === S.aiLevel) {
    $$('#difficulty button').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
  }
  btn.addEventListener('click', () => {
    $$('#difficulty button').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    S.aiLevel = btn.dataset.level;
    localStorage.setItem('sprtec2_level', S.aiLevel);
    sfx.click();
  });
});

$('#btn-sp').onclick = () => { sfx.click(); autoFullscreen(); startLocalGame('sp'); };
$('#btn-local').onclick = () => { sfx.click(); autoFullscreen(); startLocalGame('local'); };
$('#btn-online').onclick = () => { sfx.click(); autoFullscreen(); goOnline(); };

$('#btn-fs').onclick = () => { sfx.click(); toggleFullscreen(); };
if (!FS_SUPPORTED) show('#btn-fs', false);
document.addEventListener('fullscreenchange', () => {
  $('#btn-fs').title = document.fullscreenElement ? 'Ukončit celou obrazovku' : 'Celá obrazovka';
  scene.resize();
});

$('#btn-lobby-back').onclick = () => { sfx.click(); leaveToMenu(); };
$('#btn-quick').onclick = () => { sfx.click(); S.net.send({ t: 'quick' }); };
$('#btn-create').onclick = () => {
  sfx.click();
  S.net.send({ t: 'create', public: $('#create-public').checked });
};
$('#btn-join').onclick = () => {
  const code = $('#join-code').value.trim().toUpperCase();
  if (code.length !== 4) {
    toast('Kód místnosti má 4 znaky');
    return;
  }
  sfx.click();
  S.net.send({ t: 'join', code });
};
$('#join-code').addEventListener('keydown', (e) => {
  if (e.key === 'Enter') $('#btn-join').click();
});

$('#btn-copy-code').onclick = async () => {
  try {
    await navigator.clipboard.writeText($('#wait-code').textContent);
    toast('Kód zkopírován 📋');
  } catch {
    toast('Kopírování se nepovedlo');
  }
};
$('#btn-wait-cancel').onclick = () => {
  sfx.click();
  S.net.send({ t: 'leave' });
  showScreen('#screen-lobby');
};

$('#btn-leave').onclick = () => {
  sfx.click();
  if (S.mode === 'mp') S.net?.send({ t: 'leave' });
  leaveToMenu();
};
$('#btn-cam').onclick = () => {
  sfx.click();
  S.camMode = S.camMode === 'side' ? 'top' : 'side';
  scene.setCamera(S.mode === 'mp' ? S.mySide : 0, S.camMode);
};
$('#btn-sound').onclick = () => {
  sfx.ensure();
  sfx.setMuted(!sfx.muted);
  $('#btn-sound').textContent = sfx.muted ? '🔇' : '🔊';
  if (!sfx.muted) sfx.click();
};
$('#btn-sound').textContent = sfx.muted ? '🔇' : '🔊';

$$('#emote-bar button').forEach((btn) => {
  btn.addEventListener('click', () => {
    S.net?.send({ t: 'emote', k: Number(btn.dataset.emote) });
    sfx.click();
  });
});

$('#btn-rematch').onclick = () => {
  sfx.click();
  if (S.mode === 'mp') {
    S.net.send({ t: 'rematch' });
    $('#end-sub').textContent = 'Čekám na souhlas soupeře…';
  } else {
    show('#overlay-end', false);
    startLocalGame(S.mode);
  }
};
$('#btn-end-menu').onclick = () => {
  sfx.click();
  if (S.mode === 'mp') S.net?.send({ t: 'leave' });
  leaveToMenu();
};

// ---------------------------------------------------------------------------
// Hlavní smyčka
// ---------------------------------------------------------------------------

let last = performance.now();
let hudClock = 0;

function advance(dt) {
  if ((S.mode === 'sp' || S.mode === 'local') && S.game && !S.ended) {
    const events = S.game.update(dt);
    scene.setBodiesDirect(S.game.bodies);
    const phaseBefore = S.phase;
    S.phase = S.game.phase;
    S.turn = S.game.turn;
    handleEvents(events);
    if (!events.length && phaseBefore !== S.phase) updateHud();
  }

  hudClock += dt;
  if (hudClock > 0.25) {
    hudClock = 0;
    updateTimer();
  }
}

function frame(now) {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  advance(dt);
  scene.update(dt);
  requestAnimationFrame(frame);
}

// na skrytém tabu requestAnimationFrame neběží — hru posouvá interval,
// aby lokální zápas nezamrzl (a po návratu nepřeskakoval)
setInterval(() => {
  if (document.hidden) {
    last = performance.now();
    advance(0.2);
    scene.update(0.2);
  }
}, 200);

leaveToMenu();
requestAnimationFrame(frame);

// debug rozhraní pro automatizované testy
window.__S = S;
window.__scene = scene;
